import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { dashboardAPI, remindersAPI, analyticsAPI, budgetPlannerAPI, savingsAPI, liabilityAccountsAPI } from "../lib/api";
import { formatCurrency, formatDurationLong, formatDate, getPaymentStatusColor, getDueDateUrgencyColor, PAYMENT_STATUS_LABELS } from "../lib/utils";
import { useAuth } from "../contexts/AuthContext";
// Activation Flow Layer — improves first-session workspace setup and trial conversion
import ActivationFlow from "../components/ActivationFlow";
import ActivationChecklistWidget from "../components/ActivationChecklistWidget";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { 
  Clock, 
  Receipt, 
  Users, 
  FolderKanban, 
  CheckSquare,
  TrendingUp,
  DollarSign,
  Timer,
  Bell,
  AlertTriangle,
  FileText,
  Briefcase,
  CalendarDays,
  ShieldAlert,
  CreditCard,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const REMINDER_ICONS = {
  task: CheckSquare,
  project: FolderKanban,
  invoice: Receipt,
  quote: FileText,
  lead: Briefcase,
};

const REMINDER_COLORS = {
  task: "text-blue-500",
  project: "text-purple-500",
  invoice: "text-yellow-600",
  quote: "text-green-500",
  lead: "text-pink-500",
};

const RISK_COLORS = {
  CRITICAL: "text-red-600 dark:text-red-400",
  HIGH_RISK: "text-orange-600 dark:text-orange-400",
  UNSTABLE: "text-yellow-600 dark:text-yellow-400",
  STABLE: "text-green-600 dark:text-green-400",
};

const RISK_BADGE_CLASSES = {
  CRITICAL: "border-red-500/30 text-red-600 bg-red-500/10 dark:text-red-400",
  HIGH_RISK: "border-orange-500/30 text-orange-600 bg-orange-500/10 dark:text-orange-400",
  UNSTABLE: "border-yellow-500/30 text-yellow-600 bg-yellow-500/10 dark:text-yellow-400",
  STABLE: "border-green-500/30 text-green-600 bg-green-500/10 dark:text-green-400",
};

const SAVINGS_STORAGE_KEY = "savings_prev_snapshot";

export default function DashboardPage() {
  const navigate = useNavigate();
  // Activation Flow Layer — user needed to drive activation modals and checklist
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savings, setSavings] = useState(null);
  const [savingsAlert, setSavingsAlert] = useState(null);
  const [obligations, setObligations] = useState(null);
  const [cashPressure, setCashPressure] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [liabilityAccounts, setLiabilityAccounts] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, remindersRes, savingsRes, obligationsRes, cashPressureRes, accountsRes, liabilityAccountsRes] = await Promise.all([
          dashboardAPI.getStats(),
          remindersAPI.list(),
          analyticsAPI.getSavings(),
          budgetPlannerAPI.getUpcomingObligations().catch(() => ({ data: null })),
          analyticsAPI.getCashPressure().catch(() => ({ data: null })),
          savingsAPI.list().catch(() => ({ data: [] })),
          liabilityAccountsAPI.list().catch(() => ({ data: [] })),
        ]);
        setStats(statsRes.data);
        setReminders(remindersRes.data);
        setObligations(obligationsRes.data);
        setCashPressure(cashPressureRes.data);
        setAccounts(accountsRes.data || []);
        setLiabilityAccounts(liabilityAccountsRes.data || []);

        const newSavings = savingsRes.data;
        setSavings(newSavings);

        if (newSavings.has_data) {
          const prev = (() => {
            try { return JSON.parse(localStorage.getItem(SAVINGS_STORAGE_KEY)); } catch (e) { console.warn("savings snapshot parse error", e); return null; }
          })();
          if (prev) {
            const monthsDelta = Math.abs(newSavings.survival_months - prev.survival_months);
            const riskChanged = newSavings.risk_level !== prev.risk_level;
            if (monthsDelta >= 0.5 || riskChanged) {
              setSavingsAlert({ from: prev.survival_months, to: newSavings.survival_months });
            }
          }
          localStorage.setItem(SAVINGS_STORAGE_KEY, JSON.stringify({
            survival_months: newSavings.survival_months,
            risk_level: newSavings.risk_level,
          }));
        }
      } catch (err) {
        console.error("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Filter reminders for next 7 days and overdue
  const today = new Date().toISOString().split("T")[0];
  const nextWeek = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];
  
  const overdueReminders = reminders.filter(r => r.date < today);
  const upcomingReminders = reminders.filter(r => r.date >= today && r.date <= nextWeek);
  const urgentReminders = [...overdueReminders, ...upcomingReminders].slice(0, 8);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  const invoiceChartData = stats ? [
    { name: "Draft", value: stats.invoice_stats.draft, color: "#6B7280" },
    { name: "Sent", value: stats.invoice_stats.sent, color: "#F59E0B" },
    { name: "Paid", value: stats.invoice_stats.paid, color: "#10B981" },
    { name: "Overdue", value: stats.invoice_stats.overdue, color: "#EF4444" },
  ].filter(item => item.value > 0) : [];

  const hoursChartData = stats?.hours_by_client || [];

  // Build per-currency cards from Financials source-of-truth
  const collectedByCurrency = stats?.revenue_by_currency || {};
  const outstandingByCurrency = stats?.outstanding_by_currency || {};
  const allCurrencies = [...new Set([
    ...Object.keys(collectedByCurrency),
    ...Object.keys(outstandingByCurrency),
  ])].sort();

  // Build account name lookup maps for installment pressure display
  const cashAccountById = Object.fromEntries(accounts.map((a) => [a.id, a.account_label || a.bank_name || a.id]));
  const liabilityAccountById = Object.fromEntries(liabilityAccounts.map((a) => [a.id, a.name || a.id]));
  const pressureCurrency = accounts[0]?.currency || "PHP";

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard-page">
      {/* Activation Flow Layer — post-onboarding modals (settings wizard + path selection) */}
      <ActivationFlow />

      <div>
        <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
        <p className="text-muted-foreground mt-1">
          Welcome back! Here's your business overview.
        </p>
      </div>

      {/* Activation Flow Layer — getting-started checklist widget */}
      <ActivationChecklistWidget user={user} stats={stats} />

      {/* Savings Alert Banner */}
      {savingsAlert && (
        <div className="flex items-center justify-between gap-4 rounded-lg border border-orange-400/40 bg-orange-500/10 px-4 py-3 text-sm">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-orange-500 flex-shrink-0" />
            <span>
              Your financial runway changed from{" "}
              <strong>{savingsAlert.from.toFixed(1)}</strong> to{" "}
              <strong>{savingsAlert.to.toFixed(1)}</strong> months based on recent activity.
            </span>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="flex-shrink-0"
            onClick={() => navigate("/analytics?tab=savings")}
          >
            Review Savings Analysis
          </Button>
        </div>
      )}

      {/* Savings Analysis Summary Card */}
      {savings && (
        <div>
          <h2 className="text-lg font-semibold tracking-tight mb-2">Savings Analysis</h2>
          <Card className="hover-lift" data-testid="savings-analysis-card">
            <CardContent className="pt-5">
              {savings.has_data ? (
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="space-y-3 flex-1">
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-muted-foreground font-medium">Financial Runway</span>
                      <span className={`font-mono text-2xl font-bold ${RISK_COLORS[savings.risk_level]}`}>
                        {savings.survival_months.toFixed(1)} mo
                      </span>
                      <Badge
                        variant="outline"
                        className={`text-xs font-semibold ${RISK_BADGE_CLASSES[savings.risk_level]}`}
                      >
                        {savings.risk_level.replace("_", " ")}
                      </Badge>
                    </div>
                    <Progress
                      value={Math.min((savings.survival_months / 6) * 100, 100)}
                      className="h-2"
                    />
                    <div className="flex gap-6 text-xs text-muted-foreground">
                      <span>Goal: <strong className="text-foreground">6 months</strong></span>
                      {savings.gap > 0 && (
                        <span>Gap: <strong className="text-foreground">{formatCurrency(savings.gap, savings.currency)}</strong></span>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-shrink-0"
                    onClick={() => navigate("/analytics?tab=savings")}
                  >
                    <ShieldAlert className="h-4 w-4 mr-2" />
                    View Full Savings Analysis
                  </Button>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    Lock a budget plan to see your savings analysis.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate("/analytics?tab=savings")}
                  >
                    View Savings Analysis
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Accounts & Balances Summary Widget */}
      {accounts.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold tracking-tight mb-2">Accounts &amp; Balances</h2>
          <Card className="hover-lift">
            <CardContent className="pt-5">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 flex-1">
                  <div>
                    <p className="text-xs text-muted-foreground">Total Cash</p>
                    <p className="text-lg font-bold text-primary">
                      {formatCurrency(
                        accounts.reduce((s, a) => s + (a.current_balance ?? a.amount ?? 0), 0),
                        accounts[0]?.currency || "PHP"
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Spendable</p>
                    <p className="text-lg font-bold text-emerald-500">
                      {formatCurrency(
                        accounts.filter((a) => a.include_in_cashflow).reduce((s, a) => s + (a.current_balance ?? a.amount ?? 0), 0),
                        accounts[0]?.currency || "PHP"
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Protected</p>
                    <p className="text-lg font-bold text-violet-500">
                      {formatCurrency(
                        accounts.filter((a) => a.is_savings_account || ["savings","emergency","reserve"].includes(a.account_role)).reduce((s, a) => s + (a.current_balance ?? a.amount ?? 0), 0),
                        accounts[0]?.currency || "PHP"
                      )}
                    </p>
                  </div>
                </div>
                {cashPressure && cashPressure.has_shortfall && (
                  <div className="flex items-center gap-2 rounded-md bg-red-500/10 border border-red-400/30 px-3 py-2 text-sm text-red-600 dark:text-red-400 flex-shrink-0">
                    <AlertTriangle className="h-4 w-4" />
                    <span>Cash shortfall: {formatCurrency(cashPressure.shortfall, accounts[0]?.currency || "PHP")}</span>
                  </div>
                )}
                <Button variant="outline" size="sm" className="flex-shrink-0" onClick={() => navigate("/savings")}>
                  View Accounts
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Metric Cards - Bento Grid */}
      <div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
        {/* Billable Hours This Week */}
        <Card className="hover-lift" data-testid="billable-hours-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Billable Hours (Week)
            </CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="big-number text-brand-accent">
              {stats?.billable_hours_week || 0}h
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              This week's tracked billable time
            </p>
          </CardContent>
        </Card>

        {/* Today's Time */}
        <Card className="hover-lift" data-testid="today-time-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Today's Time
            </CardTitle>
            <Timer className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="big-number">
              {formatDurationLong(stats?.today_minutes || 0)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Time tracked today
            </p>
          </CardContent>
        </Card>

        {/* Active Clients */}
        <Card className="hover-lift">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Active Clients
            </CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats?.total_clients || 0}</div>
          </CardContent>
        </Card>

        {/* Pending Tasks */}
        <Card className="hover-lift">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Pending Tasks
            </CardTitle>
            <CheckSquare className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats?.pending_tasks || 0}</div>
          </CardContent>
        </Card>
      </div>

      {/* Per-Currency Financial Summary */}
      {allCurrencies.length > 0 && (
        <div className="space-y-2">
          <h2 className="text-lg font-semibold tracking-tight">Financial Summary</h2>
          <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
            {allCurrencies.map((cur) => (
              <React.Fragment key={cur}>
                {/* Revenue Collected per currency */}
                <Card className="hover-lift" data-testid={`revenue-card-${cur}`}>
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      Revenue Collected ({cur})
                    </CardTitle>
                    <TrendingUp className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="big-number text-green-600 dark:text-green-400">
                      {formatCurrency(collectedByCurrency[cur] || 0, cur)}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      From income records
                    </p>
                  </CardContent>
                </Card>

                {/* Outstanding per currency */}
                <Card className="hover-lift" data-testid={`outstanding-card-${cur}`}>
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      Outstanding ({cur})
                    </CardTitle>
                    <DollarSign className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="big-number text-yellow-600 dark:text-yellow-400">
                      {formatCurrency(outstandingByCurrency[cur] || 0, cur)}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Unpaid invoice balances
                    </p>
                  </CardContent>
                </Card>
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      {/* Fallback when no financial data yet */}
      {allCurrencies.length === 0 && (
        <div className="grid gap-6 grid-cols-1 md:grid-cols-2">
          <Card className="hover-lift" data-testid="revenue-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Revenue Collected
              </CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="big-number text-green-600 dark:text-green-400">—</div>
              <p className="text-xs text-muted-foreground mt-1">No income records yet</p>
            </CardContent>
          </Card>
          <Card className="hover-lift" data-testid="outstanding-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Outstanding
              </CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="big-number text-yellow-600 dark:text-yellow-400">—</div>
              <p className="text-xs text-muted-foreground mt-1">No unpaid invoices</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Second Row - Stats */}
      <div className="grid gap-6 grid-cols-1">
        <Card className="hover-lift">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Active Projects
            </CardTitle>
            <FolderKanban className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats?.active_projects || 0}</div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid gap-6 grid-cols-1 lg:grid-cols-2">
        {/* Hours by Client Chart */}
        <Card data-testid="hours-chart-card">
          <CardHeader>
            <CardTitle>Hours by Client (This Week)</CardTitle>
          </CardHeader>
          <CardContent>
            {hoursChartData.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={hoursChartData}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                    <XAxis 
                      dataKey="name" 
                      tick={{ fontSize: 12 }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis 
                      tick={{ fontSize: 12 }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))',
                        borderColor: 'hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                    />
                    <Bar 
                      dataKey="hours" 
                      fill="hsl(var(--brand-accent))" 
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-muted-foreground">
                No time tracked this week
              </div>
            )}
          </CardContent>
        </Card>

        {/* Invoice Pipeline Chart */}
        <Card data-testid="invoice-pipeline-card">
          <CardHeader>
            <CardTitle>Invoice Pipeline</CardTitle>
          </CardHeader>
          <CardContent>
            {invoiceChartData.length > 0 ? (
              <div className="h-64 flex items-center">
                <ResponsiveContainer width="50%" height="100%">
                  <PieChart>
                    <Pie
                      data={invoiceChartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {invoiceChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex-1 space-y-2">
                  {invoiceChartData.map((item) => (
                    <div key={item.name} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: item.color }}
                        />
                        <span className="text-sm">{item.name}</span>
                      </div>
                      <span className="font-mono font-medium">{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-muted-foreground">
                No invoices yet
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Top Clients */}
      {stats?.top_clients?.length > 0 && (
        <Card data-testid="top-clients-card">
          <CardHeader>
            <CardTitle>Top Clients by Revenue</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {stats.top_clients.map((client, index) => (
                <div key={client.id} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium">
                      {index + 1}
                    </div>
                    <span className="font-medium">{client.name}</span>
                  </div>
                  <span className="font-mono font-semibold text-green-600 dark:text-green-400">
                    {formatCurrency(client.revenue, client.currency)}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Upcoming Obligations Widget */}
      {obligations && (
        (() => {
          const hasItems =
            (obligations.overdue?.length || 0) +
            (obligations.due_today?.length || 0) +
            (obligations.due_3_days?.length || 0) +
            (obligations.due_this_week?.length || 0) > 0;

          const ObligationGroup = ({ title, items, badgeClass }) => {
            if (!items || items.length === 0) return null;
            return (
              <div className="space-y-2">
                <h4 className={`text-xs font-semibold uppercase tracking-wide ${badgeClass}`}>{title}</h4>
                {items.map((item, idx) => (
                  <div key={item.id || idx} className="flex items-center justify-between p-2 rounded-md bg-muted/50">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{item.description || item.category}</p>
                      <p className="text-xs text-muted-foreground">{item.category}{item.subcategory ? ` / ${item.subcategory}` : ""}</p>
                    </div>
                    <div className="text-right ml-3 flex-shrink-0">
                      <p className={`text-sm font-mono font-semibold ${getDueDateUrgencyColor(item.due_date)}`}>
                        {item.due_date || "—"}
                      </p>
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${getPaymentStatusColor(item.payment_status)}`}>
                        {PAYMENT_STATUS_LABELS[item.payment_status || "planned"] || (item.payment_status || "planned").replace(/_/g, " ")}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            );
          };

          return (
            <Card data-testid="upcoming-obligations-card">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  Upcoming Obligations
                </CardTitle>
                <Button variant="outline" size="sm" onClick={() => navigate("/budget-planner")}>
                  View Budget Planner
                </Button>
              </CardHeader>
              <CardContent>
                {!hasItems ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <CreditCard className="h-12 w-12 mx-auto mb-4 opacity-30" />
                    <p>No upcoming payment obligations this month</p>
                    <p className="text-sm mt-1">Add due dates to budget lines to track payments here</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <ObligationGroup
                      title="Overdue"
                      items={obligations.overdue}
                      badgeClass="text-red-600 dark:text-red-400"
                    />
                    <ObligationGroup
                      title="Due Today"
                      items={obligations.due_today}
                      badgeClass="text-orange-600 dark:text-orange-400"
                    />
                    <ObligationGroup
                      title="Due in 3 Days"
                      items={obligations.due_3_days}
                      badgeClass="text-yellow-600 dark:text-yellow-400"
                    />
                    <ObligationGroup
                      title="Due This Week"
                      items={obligations.due_this_week}
                      badgeClass="text-blue-600 dark:text-blue-400"
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })()
      )}

      {/* Installment Pressure Widget */}
      {cashPressure && (cashPressure.installment_cash_pressure > 0 || cashPressure.installment_liability_pressure > 0) && (() => {
        const cashRows = cashPressure.installment_cash_pressure_by_account || [];
        const cashAssigned = cashRows.reduce((s, e) => s + e.amount, 0);
        const cashUnassigned = Math.round((cashPressure.installment_cash_pressure - cashAssigned) * 100) / 100;

        const liabilityRows = cashPressure.installment_liability_pressure_by_account || [];
        const liabilityAssigned = liabilityRows.reduce((s, e) => s + e.amount, 0);
        const liabilityUnassigned = Math.round((cashPressure.installment_liability_pressure - liabilityAssigned) * 100) / 100;

        const PressureRow = ({ label, amount }) => (
          <div className="flex items-center justify-between text-sm px-2 py-1 rounded bg-muted/50">
            <span className="text-muted-foreground">{label}</span>
            <span className="font-mono">{formatCurrency(amount, pressureCurrency)}</span>
          </div>
        );

        return (
          <Card data-testid="installment-pressure-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                Installment Pressure (Next 7 Days)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              {cashPressure.installment_cash_pressure > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Cash-Based</h4>
                    <span className="font-mono font-semibold text-orange-600 dark:text-orange-400">
                      {formatCurrency(cashPressure.installment_cash_pressure, pressureCurrency)}
                    </span>
                  </div>
                  <div className="space-y-1">
                    {cashRows.map((entry) => (
                      <PressureRow
                        key={entry.account_id}
                        label={cashAccountById[entry.account_id] || "Unknown account"}
                        amount={entry.amount}
                      />
                    ))}
                    {cashUnassigned > 0 && (
                      <PressureRow label="Unassigned" amount={cashUnassigned} />
                    )}
                  </div>
                </div>
              )}
              {cashPressure.installment_liability_pressure > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Liability-Based</h4>
                    <span className="font-mono font-semibold text-blue-600 dark:text-blue-400">
                      {formatCurrency(cashPressure.installment_liability_pressure, pressureCurrency)}
                    </span>
                  </div>
                  <div className="space-y-1">
                    {liabilityRows.map((entry) => (
                      <PressureRow
                        key={entry.liability_account_id}
                        label={liabilityAccountById[entry.liability_account_id] || "Unknown account"}
                        amount={entry.amount}
                      />
                    ))}
                    {liabilityUnassigned > 0 && (
                      <PressureRow label="Unassigned" amount={liabilityUnassigned} />
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })()}

      {/* Reminders Section */}
      <Card data-testid="reminders-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Reminders & Deadlines
          </CardTitle>
          <Button variant="outline" size="sm" onClick={() => navigate("/calendar")}>
            <CalendarDays className="h-4 w-4 mr-2" />
            View Calendar
          </Button>
        </CardHeader>
        <CardContent>
          {urgentReminders.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Bell className="h-12 w-12 mx-auto mb-4 opacity-30" />
              <p>No upcoming reminders</p>
              <p className="text-sm mt-1">Set due dates on tasks, projects, and invoices to see them here</p>
            </div>
          ) : (
            <div className="space-y-3">
              {overdueReminders.length > 0 && (
                <div className="flex items-center gap-2 text-red-600 mb-2">
                  <AlertTriangle className="h-4 w-4" />
                  <span className="text-sm font-medium">{overdueReminders.length} overdue items</span>
                </div>
              )}
              {urgentReminders.map((reminder) => {
                const Icon = REMINDER_ICONS[reminder.type];
                const isOverdue = reminder.date < today;
                const isToday = reminder.date === today;
                
                return (
                  <div
                    key={`${reminder.type}-${reminder.id}`}
                    className={`flex items-center gap-4 p-3 rounded-lg border cursor-pointer hover:bg-muted transition-colors ${
                      isOverdue ? "border-red-300 bg-red-50/50 dark:bg-red-900/10" : ""
                    } ${isToday ? "border-blue-300 bg-blue-50/50 dark:bg-blue-900/10" : ""}`}
                    onClick={() => navigate(reminder.link)}
                  >
                    <div className={`p-2 rounded-lg bg-muted ${REMINDER_COLORS[reminder.type]}`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{reminder.title}</p>
                      {reminder.client_name && (
                        <p className="text-sm text-muted-foreground">{reminder.client_name}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className={`text-sm font-medium ${
                        isOverdue ? "text-red-600" : isToday ? "text-blue-600" : "text-muted-foreground"
                      }`}>
                        {isOverdue ? "Overdue" : isToday ? "Today" : formatDate(reminder.date)}
                      </p>
                      <Badge variant="outline" className="capitalize text-xs mt-1">
                        {reminder.type}
                      </Badge>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
